import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import Check from "react-feather/dist/icons/check";

import style from "./style.module.scss";
import { Button } from "../../../ui";
import { useScrollReveal } from "../../../hooks/use-scroll-reveal";

interface PricingSectionProps {
  price: number;
  priceLabel?: string;
  currency: string;
  includes?: string[];
  ctaLabel: string;
  ctaTo: string;
}

function AnimatedPrice({ target }: { target: number }) {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated.current) {
            hasAnimated.current = true;
            const duration = 1200;
            const start = performance.now();

            function animate(now: number) {
              const elapsed = now - start;
              const progress = Math.min(elapsed / duration, 1);
              // Ease out cubic
              const eased = 1 - Math.pow(1 - progress, 3);
              setValue(Math.round(eased * target));

              if (progress < 1) {
                requestAnimationFrame(animate);
              }
            }

            requestAnimationFrame(animate);
            observer.unobserve(element);
          }
        });
      },
      { threshold: 0.5 },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [target]);

  return <span ref={ref}>{value.toLocaleString()}</span>;
}

function PricingSection({
  price,
  priceLabel,
  currency,
  includes,
  ctaLabel,
  ctaTo,
}: PricingSectionProps) {
  const cardRef = useScrollReveal<HTMLDivElement>({ direction: "scale", delay: 100 });

  return (
    <section className={style.pricing}>
      <div className={style.pricingInner}>
        <div ref={cardRef} className={style.pricingCard}>
          <div className={style.pricingGlow} />
          <div className={style.priceDisplay}>
            {priceLabel && <span className={style.priceLabel}>{priceLabel}</span>}
            <div className={style.priceRow}>
              <span className={style.priceAmount}>
                <AnimatedPrice target={price} />
              </span>
              <span className={style.priceCurrency}>{currency}</span>
            </div>
          </div>

          {includes && includes.length > 0 && (
            <ul className={style.includesList}>
              {includes.map((item, index) => (
                <li key={index} className={style.includesItem}>
                  <Check className={style.checkIcon} />
                  {item}
                </li>
              ))}
            </ul>
          )}

          <Link to={ctaTo} className={style.pricingCtaLink}>
            <Button className={style.pricingCtaButton} isPrimary isLarge>
              {ctaLabel}
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

export default PricingSection;
